/**
 * AI body-row helpers.
 *
 * The AI generation pipeline represents a request body as a flat string
 * (`set_body` / `set_step_body.body`). For the structured body formats
 * (form-data and x-www-form-urlencoded) Imago's editors and persistence work
 * with row objects + `mode_properties`, not a raw string. These helpers convert
 * the AI's flat string into the structured rows the editors expect.
 *
 * Supported input shapes (whatever the model emits):
 *   - "key=value&key2=value2"   (url-encoded / Postman style)
 *   - "key=value" per line
 *   - "key: value" per line
 *
 * Keys and values are left as-is except for percent-decoding of `&`-joined
 * pairs; `{{variables}}` are preserved (they contain no percent-encoding).
 */

function safeDecode(str) {
  if (typeof str !== 'string') return '';
  try {
    return decodeURIComponent(str.replace(/\+/g, ' '));
  } catch {
    return str;
  }
}

/**
 * Parse a flat body string into an array of `{ key, value }` pairs.
 *
 * @param {string} raw
 * @returns {Array<{key: string, value: string}>}
 */
export function parseBodyPairs(raw) {
  if (!raw || typeof raw !== 'string') return [];
  const trimmed = raw.trim();
  if (!trimmed) return [];

  const useAmp = trimmed.includes('&') && !trimmed.includes('\n');
  const parts = useAmp ? trimmed.split('&') : trimmed.split(/\r?\n/);

  const rows = [];
  for (const part of parts) {
    if (!part || !part.trim()) continue;
    const eq = part.indexOf('=');
    const colon = part.indexOf(':');
    let sep = -1;
    if (eq !== -1 && (colon === -1 || eq < colon)) sep = eq;
    else if (colon !== -1) sep = colon;

    let key;
    let value;
    if (sep === -1) {
      key = part.trim();
      value = '';
    } else {
      key = part.slice(0, sep).trim();
      value = part.slice(sep + 1).trim();
    }
    if (useAmp) {
      key = safeDecode(key);
      value = safeDecode(value);
    }
    if (key === '') continue;
    rows.push({ key, value });
  }
  return rows;
}

/**
 * Build form-data editor rows (text-only) from an AI body string.
 *
 * @param {string} raw
 * @returns {Array<Object>}
 */
export function toFormDataRows(raw) {
  return parseBodyPairs(raw).map(({ key, value }) => ({
    key,
    value,
    type: 'text',
    description: '',
    enabled: true,
  }));
}

/**
 * Build url-encoded editor rows from an AI body string.
 *
 * @param {string} raw
 * @returns {Array<Object>}
 */
export function toUrlEncodedRows(raw) {
  return parseBodyPairs(raw).map(({ key, value }) => ({
    key,
    value,
    description: '',
    enabled: true,
  }));
}

/**
 * Coerce a headers/params value into the canonical array-of-{key,value} shape
 * the execution engine and the Edit Request modal both expect. Accepts:
 *   - an array of {key,value} rows (filtered/normalized), or
 *   - an object map like {"X-Foo":"bar"} (converted to [{key:"X-Foo",value:"bar"}]).
 * Returns null for anything else.
 *
 * @param {*} value
 * @returns {Array<{key:string,value:string}>|null}
 */
export function coerceKeyValueRows(value) {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const rows = value
      .filter((r) => r && typeof r === 'object' && r.key != null && String(r.key).length > 0)
      .map((r) => ({ key: String(r.key), value: r.value != null ? String(r.value) : '' }));
    return rows.length ? rows : null;
  }
  if (typeof value === 'object') {
    const rows = Object.entries(value)
      .filter(([k]) => k != null && String(k).length > 0)
      .map(([k, v]) => ({ key: String(k), value: v != null ? String(v) : '' }));
    return rows.length ? rows : null;
  }
  return null;
}

/**
 * Normalize an API_CALL node's parsed configJson so AI-emitted request overrides
 * land under the canonical `override*` field names in the list shape the engine
 * and the Edit Request modal read. Coerces headers/params from a map or list,
 * and migrates stray `headers`/`params`/`queryParams` onto the override fields.
 * Body/auth/script overrides are passed through unchanged (the prompt instructs
 * their exact shapes and the engine reads them directly).
 *
 * @param {Object} cfg - parsed configJson object
 * @returns {Object} normalized config object
 */
export function normalizeApiCallOverrides(cfg) {
  if (!cfg || typeof cfg !== 'object') return cfg;
  const out = { ...cfg };

  // Headers — prefer overrideHeaders, fall back to a stray `headers`.
  const rawHeaders = out.overrideHeaders != null ? out.overrideHeaders : out.headers;
  const headerRows = coerceKeyValueRows(rawHeaders);
  if (headerRows) {
    out.overrideHeaders = headerRows;
  }
  if ('headers' in out) delete out.headers;

  // Query params — prefer overrideParams, fall back to `params`/`queryParams`.
  const rawParams =
    out.overrideParams != null ? out.overrideParams : (out.params != null ? out.params : out.queryParams);
  const paramRows = coerceKeyValueRows(rawParams);
  if (paramRows) {
    out.overrideParams = paramRows;
  }
  if ('params' in out) delete out.params;
  if ('queryParams' in out) delete out.queryParams;

  // Body — if the AI emitted a raw body as `body: {content, format}`, surface it
  // as overrideBody + overrideBodyFormat:'raw' so it both executes (engine reads
  // overrideBody) and renders in the Edit Request modal (which reads overrideBody).
  // Structured formats (form-data / x-www-form-urlencoded) are handled separately
  // by normalizeNodeBodies (-> overrideBodyFormat + mode_properties) and are
  // intentionally excluded here.
  if (out.overrideBody == null && out.body && typeof out.body === 'object') {
    const fmt = out.body.format;
    const content = out.body.content;
    if (typeof content === 'string' && (fmt == null || fmt === 'raw' || fmt === 'json' || fmt === 'text')) {
      out.overrideBody = content;
      if (out.overrideBodyFormat == null) out.overrideBodyFormat = 'raw';
    }
  }

  // Align naming: the engine and the Edit Request modal read `mode_properties`,
  // but the manual editor's save emits `overrideModeProperties`. If only the
  // latter is present, mirror it so structured bodies execute and display.
  if (out.mode_properties == null && out.overrideModeProperties != null) {
    out.mode_properties = out.overrideModeProperties;
  }

  return out;
}

/**
 * Build a `mode_properties` object for persistence from an AI body string.
 * Returns `{ formData: [...] }` or `{ urlEncoded: [...] }` matching the shape
 * the backend `ModePropertiesCodec` deserializes.
 *
 * @param {string} format - "form-data" | "x-www-form-urlencoded"
 * @param {string} raw
 * @returns {Object|null}
 */
export function toModeProperties(format, raw) {
  if (format === 'form-data') {
    return {
      formData: parseBodyPairs(raw).map(({ key, value }) => ({
        key,
        value,
        type: 'text',
        enabled: true,
        description: '',
      })),
    };
  }
  if (format === 'x-www-form-urlencoded') {
    return {
      urlEncoded: parseBodyPairs(raw).map(({ key, value }) => ({
        key,
        value,
        enabled: true,
        description: '',
      })),
    };
  }
  return null;
}
